/**
 * CheckNode
 *
 * Represents an additional background check step that runs after identity proof
 * is confirmed. Each check type has its own icon and brand color.
 *
 * The three supported check types and their visual treatment:
 *   - global_watchlist          → orange icon, Global Watchlist icon
 *   - authoritative_db          → blue icon,   AuthDB icon  (also shows "Quick Verify" subtitle)
 *   - seven_year_criminal_report → orange icon, 7-Year Criminal icon
 *
 * Position in flowchart (below the Identity Proof decision node):
 *   [Identity Proof Valid? ✓]
 *          ↓ Yes
 *   [GlobalWatchlist]   [AuthDB]
 *          ↓                ↓
 *   [Decision]         [Decision]
 *                           ↓ Yes
 *                      [SevenYear]
 *
 * Icon and icon background color are resolved dynamically from ICON_MAP / COLOR_MAP
 * based on data.type. Color is applied as an inline style so it can vary per instance.
 *
 * Handles:
 *   - target-top:    receives the incoming edge from the Identity Proof decision (Yes branch)
 *   - source-bottom: connects downward to the check's own decision node
 *
 * Data props:
 *   - type:     check type key ('global_watchlist' | 'authoritative_db' | 'seven_year_criminal_report')
 *   - label:    display name (e.g. "Global Watchlist Check")
 *   - subtitle: optional secondary label shown below the main label (e.g. "Quick Verify")
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { Handle, Position } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

import { ReactComponent as SevenYearCheck } from 'asset/CustomIcons/icon-7year-criminal-activity-check.svg';
import { ReactComponent as AuthDB } from 'asset/CustomIcons/icon-authdb.svg';
import { ReactComponent as GlobalWatchlistCheck } from 'asset/IconsPack/at_global_watchlist.svg';

const ICON_MAP = {
    global_watchlist: GlobalWatchlistCheck,
    authoritative_db: AuthDB,
    seven_year_criminal_report: SevenYearCheck,
};

const COLOR_MAP = {
    global_watchlist: '#DC6803',
    authoritative_db: '#1570EF',
    seven_year_criminal_report: '#DC6803',
};

function CheckNode({ data }) {
    const Icon = ICON_MAP[data.type] || GlobalWatchlistCheck;
    const color = COLOR_MAP[data.type] || '#DC6803';

    return (
        <Box className={styles.checkNode}>
            <Box className={styles.checkIconWrapper} style={{ backgroundColor: color }}>
                <Icon className={styles.checkIcon} />
            </Box>
            <Box className={styles.checkLabelWrapper}>
                <Typography component="span" className={styles.checkLabel}>
                    {data.label}
                </Typography>
                {data.subtitle && (
                    <Typography component="span" className={styles.checkSubtitle}>
                        {data.subtitle}
                    </Typography>
                )}
            </Box>

            {/* Receives edge from Identity Proof decision (Yes) or from previous check (AuthDB → SevenYear) */}
            <Handle type="target" position={Position.Top} id="target-top" style={{ opacity: 0 }} />

            {/* Connects downward to this check's own decision node */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="source-bottom"
                style={{ opacity: 0 }}
            />
        </Box>
    );
}

CheckNode.propTypes = {
    data: PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subtitle: PropTypes.string,
    }).isRequired,
};

export default CheckNode;
